/**
 * Legacy landing URLs that render the same content as a stronger canonical URL.
 *
 * Keep this list aligned with backend/main.py SEO_REDIRECTS and the Nginx
 * landing-alias map. These routes are intentionally omitted from the sitemap.
 */
export const LANDING_CANONICAL_ALIASES = {
    // Legacy localized organic-traffic slugs now consolidate into the
    // translated routes that are actually rendered and indexed.
    '/de/buy-organic-traffic': '/de/organischen-traffic-kaufen',
    '/es/buy-organic-traffic': '/es/comprar-trafico-organico',
    '/fr/buy-organic-traffic': '/fr/acheter-trafic-organique',
    '/id/buy-organic-traffic': '/id/beli-traffic-organik',
    '/it/buy-organic-traffic': '/it/comprare-traffico-organico',
    '/pt/buy-organic-traffic': '/pt/comprar-trafego-organico',
    '/ru/buy-organic-traffic': '/ru/kupit-organicheskiy-trafik',
    '/tr/buy-organic-traffic': '/tr/organik-hit-satin-al',
    '/zh/buy-organic-traffic': '/zh/goumai-ziran-liuliang',

    // The old machine-generated "buy-web-traffic" translations were never
    // routed. Prefer the corresponding localized website-traffic page where
    // one exists, otherwise retain the exact English family canonical.
    '/ar/buy-web-traffic': '/ar/shira-traffic-mawqi',
    '/bn/buy-web-traffic': '/bn/website-traffic-kinun',
    '/cs/buy-web-traffic': '/buy-web-traffic',
    '/es/buy-web-traffic': '/es/comprar-trafico-web',
    '/fr/buy-web-traffic': '/fr/acheter-du-trafic-web',
    '/he/buy-web-traffic': '/buy-web-traffic',
    '/hi/buy-web-traffic': '/hi/website-traffic-kharidein',
    '/id/buy-web-traffic': '/id/beli-traffic-website',
    '/it/buy-web-traffic': '/it/comprare-traffico-sito-web',
    '/ja/buy-web-traffic': '/ja/buy-website-traffic',
    '/ko/buy-web-traffic': '/ko/buy-website-traffic',
    '/nl/buy-web-traffic': '/nl/website-verkeer-kopen',
    '/pl/buy-web-traffic': '/pl/kupic-ruch-na-stronie',
    '/pt/buy-web-traffic': '/pt/comprar-trafego-site',
    '/ro/buy-web-traffic': '/buy-web-traffic',
    '/ru/buy-web-traffic': '/ru/kupit-trafik-na-sayt',
    '/sv/buy-web-traffic': '/sv/kopa-webbplatstrafik',
    '/th/buy-web-traffic': '/th/sue-traffic-website',
    '/tl/buy-web-traffic': '/buy-web-traffic',
    '/tr/buy-web-traffic': '/tr/web-sitesi-trafigi-satin-al',
    '/vi/buy-web-traffic': '/vi/mua-traffic-website',
    '/zh/buy-web-traffic': '/zh/goumai-wangzhan-liuliang',

    // Unsupported legacy hits/website translations fall back to their exact
    // English canonical unless a real localized website-traffic route exists.
    '/ar/buy-website-hits': '/ar/shira-traffic-mawqi',
    '/bn/buy-website-hits': '/bn/website-traffic-kinun',
    '/cs/buy-website-hits': '/buy-website-hits',
    '/he/buy-website-hits': '/buy-website-hits',
    '/hi/buy-website-hits': '/hi/website-traffic-kharidein',
    '/id/buy-website-hits': '/id/beli-traffic-website',
    '/ro/buy-website-hits': '/buy-website-hits',
    '/th/buy-website-hits': '/th/sue-traffic-website',
    '/tl/buy-website-hits': '/buy-website-hits',
    '/vi/buy-website-hits': '/vi/mua-traffic-website',
    '/cs/buy-website-traffic': '/buy-website-traffic',
    '/he/buy-website-traffic': '/buy-website-traffic',
    '/ro/buy-website-traffic': '/buy-website-traffic',
    '/tl/buy-website-traffic': '/buy-website-traffic',

    // Plural legacy bot routes consolidate into the real localized service.
    '/ar/traffic-bots': '/ar/bot-traffic',
    '/bn/traffic-bots': '/bn/traffic-bot',
    '/cs/traffic-bots': '/cs/traffic-bot',
    '/he/traffic-bots': '/he/traffic-bot',
    '/hi/traffic-bots': '/hi/traffic-bot',
    '/pl/traffic-bots': '/pl/traffic-bot',
    '/ro/traffic-bots': '/ro/traffic-bot',
    '/th/traffic-bots': '/th/traffic-bot',
    '/tl/traffic-bots': '/tl/traffic-bot',

    '/de/buy-websites-with-traffic': '/de/websites-mit-traffic-kaufen',
    '/es/buy-websites-with-traffic': '/es/comprar-sitios-web-con-trafico',
    '/fr/buy-websites-with-traffic': '/fr/acheter-sites-web-avec-traffic',
    '/optimized-landing-page': '/',
    '/it/traffico-web-economico': '/it/traffico-sito-economico',
    '/ja/feiku-tora-fikku-wo-kounyuu-suru': '/ja/buy-fake-traffic',
    '/ja/yasui-website-traffic': '/ja/cheap-website-traffic',
    '/ko/gajja-teurapik-gumae': '/ko/buy-fake-traffic',
    '/landing2': '/free-website-traffic',
    '/pt/comprar-sites-com-trafego': '/pt/buy-websites-with-traffic',
    '/reviews/mediamister-legit': '/alternatives/media-mister',
    '/ru/kupit-desheviy-trafik': '/ru/deshevyy-trafik-na-sayt',
    '/ru/kupit-fiktivnyj-trafik': '/ru/kupit-fake-trafik',
    '/tr/ucuz-website-trafigi': '/tr/ucuz-web-sitesi-trafigi',
    '/zh/bendi-seo-liuliang-jiqiren': '/zh/bendi-seo-dianji-jiqiren',
    '/zh/lianjia-wangzhan-liuliang': '/zh/goumai-lianjia-liuliang',
} as const;

/** Public design-led landing routes with unique content and metadata. */
export const EXPERIMENTAL_LANDING_ROUTES = [
    '/modern-traffic',
] as const;

export const normalizeLandingPath = (pathname: string): string => (
    pathname === '/' ? '/' : pathname.replace(/\/+$/, '')
);

export const getCanonicalLandingPath = (pathname: string): string | null => {
    const normalizedPath = normalizeLandingPath(pathname);
    return LANDING_CANONICAL_ALIASES[
        normalizedPath as keyof typeof LANDING_CANONICAL_ALIASES
    ] ?? null;
};
